import { useMemo } from "react";
import type { NodeV2Data } from "@/types/spotflow-v2";
import { SOURCE_ONLY_TYPES } from "@/components/SpotflowV2/constants";
import { usesConfigDrivenType } from "@/components/SpotflowV2/Sidebar/mentionOptions";
import { parseFlowInputType } from "@/components/SpotflowV2/flowTypes";

export interface PickerVisibleOutput {
  name: string;
  label: string;
  originalLabel: string;
  dataType: string;
  isList: boolean;
}

/**
 * Compute visible outputs for picker source expansion and effective outputs
 * (with list propagation applied).
 */
export function useNodeOutputs(
  data: NodeV2Data,
  regEntry:
    | { inputs?: unknown[]; outputs?: unknown[]; output_paths?: string[] }
    | undefined,
  gathersListNode: boolean,
  receivesListInput: boolean
): {
  pickerVisibleOutputs: PickerVisibleOutput[];
  effectiveOutputs: PickerVisibleOutput[];
} {
  const isSourceOnly = SOURCE_ONLY_TYPES.has(data.nodeType);
  // An Input node encodes list-ness in `type` (`list[object]`); an Output node
  // keeps the element in `type` and list-ness in `is_list`. Decode both so the
  // element type never leaks as a raw `list[...]` string.
  const rawConfigType = data.nodeConfig?.type as string | undefined;
  const parsedConfigType = parseFlowInputType(rawConfigType);
  const configDrivenOutputType = rawConfigType
    ? parsedConfigType.dataType
    : undefined;
  const configDrivenIsList =
    !!data.nodeConfig?.is_list || parsedConfigType.isList;

  const pickerVisibleOutputs = useMemo(() => {
    const outputLabels = (data.nodeConfig?.__output_labels ?? {}) as Record<
      string,
      string
    >;
    const inputNodeName =
      isSourceOnly && data.nodeConfig?.label
        ? (data.nodeConfig.label as string)
        : undefined;

    if (!regEntry)
      return [
        {
          name: "output",
          label: outputLabels["output"] || inputNodeName || "Output",
          originalLabel: "Output",
          dataType: "object",
          isList: configDrivenIsList,
        },
      ];
    const connectors = (regEntry.outputs ?? []) as Array<{
      name: string;
      label?: string;
      definition?: { data_type?: string; is_list?: boolean };
      visible?: boolean;
      type?: string;
      keys?: string[];
    }>;
    // Routing nodes expose their named branches via `output_paths` (rendered as
    // handles) — these aren't readable variables, so hide their connectors.
    const hasRoutingPaths = (regEntry.output_paths ?? []).length > 0;
    const routingHandleNames = new Set(regEntry.output_paths ?? []);
    const visible = connectors.filter(
      (o) => o.visible !== false && o.name && !routingHandleNames.has(o.name)
    );
    // Pure routing nodes (no readable outputs) — don't fall back to a generic
    // "Output" pseudo-connector. Their value is the routing branch itself.
    if (visible.length === 0 && hasRoutingPaths) return [];
    if (visible.length === 0)
      return [
        {
          name: "output",
          label: outputLabels["output"] || inputNodeName || "Output",
          originalLabel: "Output",
          dataType: "object",
          isList: configDrivenIsList,
        },
      ];
    return visible.flatMap((o) => {
      const baseDataType =
        o.definition?.data_type ??
        (o as { data_type?: string }).data_type ??
        "object";
      const baseIsList =
        o.definition?.is_list ?? (o as { is_list?: boolean }).is_list ?? false;
      const useConfigType =
        usesConfigDrivenType(o, data.nodeType) && !!configDrivenOutputType;
      const dataType = useConfigType ? configDrivenOutputType : baseDataType;
      const isList = useConfigType ? configDrivenIsList : baseIsList;

      // Variable connector outputs — one sub-output per key.
      // Priority: override.keys (from config_endpoint response) >
      // nodeConfig.outputs (user-defined schema, e.g. JsonSchemaBuilder) >
      // nodeConfig.__var_keys (legacy cache).
      const connType = o.type;
      if (
        connType === "variable_type_size_connector" ||
        connType === "variable_connector"
      ) {
        if (o.keys && o.keys.length > 0) {
          return o.keys.map((k) => ({
            name: k,
            label: outputLabels[k] || k,
            originalLabel: k,
            dataType,
            isList,
          }));
        }
        const configOutputs = data.nodeConfig?.outputs as
          | Array<Record<string, string>>
          | undefined;
        if (configOutputs && Array.isArray(configOutputs)) {
          const fieldNames = configOutputs
            .map((f) => f.Name || f.name || f.label)
            .filter(Boolean);
          if (fieldNames.length > 0) {
            return fieldNames.map((fieldName) => ({
              name: fieldName,
              label: outputLabels[fieldName] || fieldName,
              originalLabel: fieldName,
              dataType,
              isList,
            }));
          }
        }
        const varKeys = (
          data.nodeConfig?.__var_keys as Record<string, string[]> | undefined
        )?.[o.name];
        if (varKeys && varKeys.length > 0) {
          return varKeys.map((k) => ({
            name: k,
            label: outputLabels[k] || k,
            originalLabel: k,
            dataType,
            isList,
          }));
        }
        // No keys anywhere — hide this connector entirely. The GSpreadsheetReader
        // case wants zero outputs until a sheet is selected rather than a stray
        // generic "data" handle.
        return [];
      }

      const originalLabel = o.label || o.name;
      const customLabel =
        outputLabels[o.name] || inputNodeName || originalLabel;
      return {
        name: o.name,
        label: customLabel,
        originalLabel,
        dataType,
        isList,
      };
    });
  }, [
    regEntry,
    configDrivenOutputType,
    configDrivenIsList,
    isSourceOnly,
    data.nodeType,
    data.nodeConfig,
  ]);

  const effectiveOutputs = useMemo(() => {
    if (!receivesListInput && !gathersListNode) return pickerVisibleOutputs;
    return pickerVisibleOutputs.map((o) => ({ ...o, isList: true }));
  }, [pickerVisibleOutputs, receivesListInput, gathersListNode]);

  return { pickerVisibleOutputs, effectiveOutputs };
}
